import React, { useContext, useCallback, useState, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import useToggle from 'hooks/useToggle'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'
import { GlobalSearchStateContext, GlobalSearchDispatchContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import Dropdown from 'components/Luxkit/Dropdown/Dropdown'
import SearchFormTextField from 'components/SearchV2/Components/SearchFormField/SearchFormTextField'
import DropdownContainer from 'components/Luxkit/Dropdown/DropdownContainer'
import Pane from 'components/Common/Pane/Pane'

import CruiseLocationContent from './CruiseLocationContent'
import CruiseFilterFooter from '../Common/CruiseFilterFooter'

const LayoutContainer = styled.div`
  padding: ${rem(20)} ${rem(24)};

  &.mobile-mode {
    padding: ${rem(20)} 0;
  }
`

const SET_SEARCH_ITEMS = GlobalSearchStateActions.SET_SEARCH_ITEMS

interface Props {
  dropdownAnchorRef?: React.RefObject<HTMLElement>
  onChange: (values: App.CruiseGlobalFilters) => void;
}

function CruiseLocationDesktopInput({ dropdownAnchorRef, onChange }: Props) {
  const inputTriggerRef = useRef<HTMLDivElement|null>(null)

  const globalFilters = useContext(GlobalSearchStateContext)
  const { searchItems = [] } = globalFilters
  const searchDispatch = useContext(GlobalSearchDispatchContext)

  const [searchTerm, setSearchTerm] = useState('')
  const [isAllSelected, setIsAllSelected] = useState(false)
  const [isOpen, toggle, show, close] = useToggle(false)

  const [facets] = useCruiseSearchFacets({ facetTypes: ['destination_ports'] })

  const locations = useMemo(() => {
    if (!searchTerm) return facets.filter(({ isTrend }) => isTrend)
    return facets.filter(({ name }) => name.toLowerCase().includes(searchTerm.toLowerCase())).slice(0, 100)
  }, [searchTerm, facets])

  const inputValue = useMemo(() => {
    if (isOpen) return searchTerm
    if (searchItems.length > 1) {
      const names = searchItems.slice(0, 1).map((item) => item.format.mainText).join(', ')
      return `${names} + ${searchItems.length - 1}`
    }
    return searchItems.map((item) => item.format.mainText).join(', ')
  }, [searchItems, isOpen, searchTerm])

  const handleOnApply = useCallback(() => {
    onChange({ searchItems })
    close()
  }, [onChange, searchItems, close])

  const handleOnClear = useCallback(() => {
    searchDispatch({ type: SET_SEARCH_ITEMS, searchItems: [] })
    setSearchTerm('')
    setIsAllSelected(false)
  }, [searchDispatch])

  const handleOnChange = useCallback((searchItems: App.SearchItem[]) => {
    searchDispatch({ type: SET_SEARCH_ITEMS, searchItems })
    setIsAllSelected(false)
  }, [searchDispatch])

  const handleSelectAll = useCallback(() => {
    searchDispatch({ type: SET_SEARCH_ITEMS, searchItems: [] })
    setIsAllSelected(!isAllSelected)
  }, [searchDispatch, isAllSelected])

  const handlerSearchTerm = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }, [])

  const handlerClearSearchTerm = useCallback(() => setSearchTerm(''), [])

  return <>
    <div ref={inputTriggerRef}>
      <SearchFormTextField
        label="Cruising to"
        placeholder={isOpen ? 'Search destinations' : 'All destinations'}
        value={inputValue}
        displayValue={inputValue}
        onChange={handlerSearchTerm}
        onClear={handlerClearSearchTerm}
        onClick={show}
      />
    </div>

    <Dropdown
      triggerRef={inputTriggerRef}
      anchorRef={dropdownAnchorRef}
      open={isOpen}
      onClose={toggle}
      floatingStrategy="absolute"
      width="100%"
      position="bottom"
      disableFlip
    >
      <DropdownContainer fit="container">
        <CruiseLocationContent
          locations={locations}
          searchTerm={searchTerm}
          searchItems={searchItems}
          isAllSelected={isAllSelected}
          onChange={handleOnChange}
          onSelectAll={handleSelectAll}
          onClose={close}
        />

        <Pane shadow>
          <LayoutContainer>
            <CruiseFilterFooter
              onClear={handleOnClear}
              onApply={handleOnApply}
            />
          </LayoutContainer>
        </Pane>
      </DropdownContainer>
    </Dropdown>
  </>
}

export default CruiseLocationDesktopInput
